
import { useState, useEffect, useCallback } from 'react'
import useSWR, { mutate } from 'swr'
import {
  KeyRound, Plus, Copy, CheckCircle2, Eye, EyeOff, Trash2,
  ToggleLeft, ToggleRight, Shield, Clock, Hash, Zap,
  AlertTriangle, X, RefreshCw, ExternalLink, Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { KEY_PERMISSIONS } from '@/lib/constants'

interface ApiKey {
  id: string
  name: string
  fingerprint: string
  permissions: string
  provider_id: string | null
  last_used_at: string | null
  created_at: string
  expires_at: string | null
  is_active: number
  request_count: number
}

interface NewKeyData {
  id: string
  raw: string
  fingerprint: string
  name: string
  permissions: string[]
  createdAt: string
  expiresAt: string | null
}

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((r) => r.json())

function CopyButton({ text, size = 'sm' }: { text: string; size?: 'sm' | 'xs' }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied
        ? <CheckCircle2 className={size === 'xs' ? 'w-3 h-3 text-success' : 'w-3.5 h-3.5 text-success'} />
        : <Copy className={size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      }
    </button>
  )
}

function PermissionBadge({ perm }: { perm: string }) {
  const colors: Record<string, string> = {
    admin: 'bg-destructive/10 text-destructive border-destructive/20',
    'ai:stream': 'bg-lava/10 text-lava border-lava/20',
    'ai:embed': 'bg-warning/10 text-warning border-warning/20',
    'db:read': 'bg-cyan-accent/10 text-cyan-accent border-cyan-accent/20',
    'db:write': 'bg-cyan-accent/10 text-cyan-accent border-cyan-accent/20',
    'storage:read': 'bg-success/10 text-success border-success/20',
    'storage:write': 'bg-success/10 text-success border-success/20',
  }
  return (
    <Badge variant="outline" className={`text-[9px] px-1.5 h-4 font-mono ${colors[perm] ?? 'border-border text-muted-foreground'}`}>
      {perm}
    </Badge>
  )
}

function KeyCard({ apiKey, onRevoke, onToggle }: {
  apiKey: ApiKey
  onRevoke: (id: string) => void
  onToggle: (id: string, active: boolean) => void
}) {
  const [showFingerprint, setShowFingerprint] = useState(false)
  const permissions: string[] = JSON.parse(apiKey.permissions || '[]')
  const isExpired = apiKey.expires_at && new Date(apiKey.expires_at) < new Date()
  const isActive = apiKey.is_active === 1 && !isExpired

  const formatDate = (d: string | null) =>
    d ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(d)) : '—'

  return (
    <Card className={`bg-card border transition-all ${isActive ? 'border-border hover:border-lava/30' : 'border-border/50 opacity-60'}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="p-1.5 rounded bg-lava/10 flex-shrink-0">
                <KeyRound className="w-3.5 h-3.5 text-lava" />
              </div>
              <h3 className="font-medium text-sm text-foreground truncate">{apiKey.name}</h3>
              {!isActive && (
                <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${isExpired ? 'border-warning/40 text-warning bg-warning/5' : 'border-muted-foreground/40 text-muted-foreground'}`}>
                  {isExpired ? 'Expired' : 'Disabled'}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1.5 bg-muted/60 border border-border rounded px-2 py-1">
                <Hash className="w-3 h-3 text-muted-foreground" />
                <code className="text-[10px] font-mono text-muted-foreground">
                  {showFingerprint ? apiKey.fingerprint : '••••••••'}
                </code>
                <button onClick={() => setShowFingerprint(v => !v)} className="text-muted-foreground hover:text-foreground transition-colors">
                  {showFingerprint ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
                {showFingerprint && <CopyButton text={apiKey.fingerprint} size="xs" />}
              </div>
            </div>

            <div className="flex flex-wrap gap-1 mt-2">
              {permissions.map(p => <PermissionBadge key={p} perm={p} />)}
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium">Created</p>
                <p className="text-xs text-foreground mt-0.5">{formatDate(apiKey.created_at)}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium">Last Used</p>
                <p className="text-xs text-foreground mt-0.5">{formatDate(apiKey.last_used_at)}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-medium">Requests</p>
                <p className="text-xs text-foreground font-mono mt-0.5">{apiKey.request_count.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="w-7 h-7 text-muted-foreground hover:text-foreground"
              onClick={() => onToggle(apiKey.id, !isActive)}
              title={isActive ? 'Disable key' : 'Enable key'}
            >
              {isActive
                ? <ToggleRight className="w-4 h-4 text-success" />
                : <ToggleLeft className="w-4 h-4" />
              }
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="w-7 h-7 text-muted-foreground hover:text-destructive transition-colors"
              onClick={() => onRevoke(apiKey.id)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function KeysPage() {
  const { data, isLoading, mutate: refresh } = useSWR<{ keys: ApiKey[] }>('/api/keys', fetcher)
  const [createOpen, setCreateOpen] = useState(false)
  const [newKeyData, setNewKeyData] = useState<NewKeyData | null>(null)
  const [revokeId, setRevokeId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Create form state
  const [keyName, setKeyName] = useState('')
  const [selectedPerms, setSelectedPerms] = useState<string[]>(['ai:stream'])
  const [expiresIn, setExpiresIn] = useState<string>('never')

  const togglePerm = (perm: string) =>
    setSelectedPerms(v => v.includes(perm) ? v.filter(p => p !== perm) : [...v, perm])

  async function handleCreate() {
    if (!keyName.trim()) return
    setCreating(true)
    try {
      const res = await fetch('/api/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: keyName,
          permissions: selectedPerms,
          expiresInDays: expiresIn === 'never' ? null : Number(expiresIn),
        }),
      })
      const d = await res.json()
      if (res.ok) {
        setNewKeyData(d.key)
        setCreateOpen(false)
        setKeyName('')
        setSelectedPerms(['ai:stream'])
        refresh()
      }
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(id: string) {
    await fetch(`/api/keys/${id}`, { method: 'DELETE' })
    setRevokeId(null)
    refresh()
  }

  async function handleToggle(id: string, active: boolean) {
    await fetch(`/api/keys/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: active }),
    })
    refresh()
  }

  const keys = data?.keys ?? []
  const activeCount = keys.filter(k => k.is_active === 1).length

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <KeyRound className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">API Keys</h2>
            <p className="text-xs text-muted-foreground">
              {activeCount} active · {keys.length} total · Web Crypto HMAC-SHA256 signed
            </p>
          </div>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          size="sm"
          className="gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90 shadow-sm"
        >
          <Plus className="w-3.5 h-3.5" /> Generate Key
        </Button>
      </div>

      {/* Security notice */}
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
        <Shield className="w-4 h-4 text-cyan-accent flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-foreground">Keyless Architecture</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Keys are signed with Web Crypto HMAC-SHA256, hashed with SHA-256 for storage, and managed by the Cloudflare Worker.
            The raw key is shown once and never stored. Only the SHA-256 hash and fingerprint are kept in D1.
          </p>
        </div>
      </div>

      {/* Keys list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-1/3 mb-3" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : keys.length === 0 ? (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-12 h-12 rounded-full bg-lava/10 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-5 h-5 text-lava" />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-1">No API keys yet</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Generate your first key to start integrating with the LavaFlow AI gateway
            </p>
            <Button
              onClick={() => setCreateOpen(true)}
              size="sm"
              className="lava-gradient text-lava-foreground border-0 gap-2 hover:opacity-90"
            >
              <Plus className="w-3.5 h-3.5" /> Generate First Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <KeyCard key={k.id} apiKey={k} onRevoke={setRevokeId} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {/* Create key dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <div className="p-1.5 rounded bg-lava/10">
                <KeyRound className="w-3.5 h-3.5 text-lava" />
              </div>
              Generate New API Key
            </DialogTitle>
            <DialogDescription className="text-xs">
              Your key will be shown once. Store it securely — it cannot be retrieved again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Key Name</Label>
              <Input
                placeholder="e.g. Production Gateway, Dev Testing"
                value={keyName}
                onChange={e => setKeyName(e.target.value)}
                className="h-8 text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">Permissions</Label>
              <div className="grid grid-cols-2 gap-1.5 p-3 rounded-md bg-muted/50 border border-border">
                {KEY_PERMISSIONS.map(p => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer group">
                    <Checkbox
                      checked={selectedPerms.includes(p.id)}
                      onCheckedChange={() => togglePerm(p.id)}
                      className="w-3.5 h-3.5 data-[state=checked]:bg-lava data-[state=checked]:border-lava"
                    />
                    <div>
                      <p className="text-[11px] font-mono font-medium group-hover:text-foreground transition-colors">{p.id}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Expires</Label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never" className="text-xs">Never expires</SelectItem>
                  <SelectItem value="30" className="text-xs">30 days</SelectItem>
                  <SelectItem value="90" className="text-xs">90 days</SelectItem>
                  <SelectItem value="365" className="text-xs">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCreate}
              disabled={creating || !keyName.trim() || selectedPerms.length === 0}
              className="w-full h-8 lava-gradient text-lava-foreground border-0 text-xs gap-2 hover:opacity-90"
            >
              {creating ? (
                <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Generating...</>
              ) : (
                <><Zap className="w-3.5 h-3.5" /> Generate Key</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New key reveal dialog */}
      {newKeyData && (
        <Dialog open onOpenChange={() => setNewKeyData(null)}>
          <DialogContent className="max-w-lg bg-card border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-success" />
                API Key Generated
              </DialogTitle>
              <DialogDescription className="text-xs">
                Copy your key now. It will never be shown again.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="p-3 rounded-lg border-2 border-lava/30 bg-lava/5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-lava uppercase tracking-wider font-semibold">API Key — Copy Now</span>
                  <CopyButton text={newKeyData.raw} />
                </div>
                <code className="text-xs font-mono text-foreground break-all leading-relaxed">{newKeyData.raw}</code>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded bg-muted/50 border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Fingerprint</p>
                  <code className="font-mono text-foreground">{newKeyData.fingerprint}</code>
                </div>
                <div className="p-2 rounded bg-muted/50 border border-border">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Permissions</p>
                  <div className="flex flex-wrap gap-1">
                    {newKeyData.permissions.map(p => (
                      <span key={p} className="text-[9px] font-mono text-lava">{p}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-2.5 rounded-md bg-warning/8 border border-warning/20">
                <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-warning-foreground">
                  Store this key in your Secrets Store or environment variables. The raw key is not stored and cannot be recovered.
                </p>
              </div>

              <Button
                onClick={() => setNewKeyData(null)}
                variant="outline"
                className="w-full h-8 text-xs border-border"
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> I have saved my key
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeId} onOpenChange={() => setRevokeId(null)}>
        <AlertDialogContent className="bg-card border-border max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm">Revoke API Key?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              This is permanent. Any services using this key will immediately lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="h-8 text-xs border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeId && handleRevoke(revokeId)}
              className="h-8 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90 border-0"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
